function numericRunId(x) {
  if (x == null || x === '') return -Infinity
  const n = Number(x)
  return Number.isFinite(n) ? n : -Infinity
}

/** Pick the AI run with the highest numeric id (latest run). */
export function pickLatestAiSimulationRun(runs) {
  if (!Array.isArray(runs) || runs.length === 0) return null
  return runs.reduce((best, cur) =>
    numericRunId(cur?.id) >= numericRunId(best?.id) ? cur : best
  )
}

/** Model id(s) recorded for one ai_simulation_run_models row, or null if not recorded.
 *  The engine writes the platform slug when it captured no id, so model === platform means none.
 *  Never derive this from config; config changes over time. */
export function getRecordedModelId(row) {
  const model = typeof row?.model === 'string' ? row.model.trim() : ''
  const platform = typeof row?.platform === 'string' ? row.platform.trim() : ''
  if (!model || (platform && model.toLowerCase() === platform.toLowerCase())) return null
  return model
}

/** Standard model per platform, ONLY for runs that saved no model id. Before 10.07.2026
 *  only ChatGPT existed, on gpt-5.2 since 06.02.2026. Frozen history: do not update it
 *  when the engine config changes. This is the only model id list in the frontend. */
export const LEGACY_DEFAULT_MODEL_ID = Object.freeze({
  chatgpt: 'gpt-5.2',
  claude: 'claude-sonnet-4-6',
  gemini: 'gemini-3.5-flash',
  perplexity: 'sonar',
})

/** Short display label for a model id, built by one rule instead of a fixed list:
 *  lowercase and trim; strip a leading "models/"; strip a trailing date (-YYYY-MM-DD or
 *  -YYYYMMDD) and a trailing 3-digit revision (-001); strip a leading "claude-" or "gemini-";
 *  split on "-"; join neighbouring purely numeric tokens with a dot; write "gpt" as "GPT";
 *  capitalise the first letter of every other word; join with spaces.
 *    gpt-5.2 -> GPT 5.2                  gpt-5.2-2025-12-11 -> GPT 5.2
 *    gpt-5.1 -> GPT 5.1                  claude-sonnet-4-6 -> Sonnet 4.6
 *    claude-sonnet-4-5-20250929 -> Sonnet 4.5
 *    claude-opus-5 -> Opus 5             gemini-3.5-flash -> 3.5 Flash
 *    gemini-2.5-flash -> 2.5 Flash       sonar -> Sonar
 *    models/gemini-2.5-flash -> 2.5 Flash  gemini-2.0-flash-001 -> 2.0 Flash */
export function formatModelLabel(id) {
  const cleaned = (typeof id === 'string' ? id.trim().toLowerCase() : '')
    .replace(/^models\//, '')
    .replace(/-(\d{4}-\d{2}-\d{2}|\d{8})$/, '')
    .replace(/-\d{3}$/, '')
    .replace(/^(claude|gemini)-/, '')
  const words = []
  let lastWasNumeric = false
  cleaned.split('-').forEach((token) => {
    if (!token) return
    const numeric = /^\d+$/.test(token)
    if (numeric && lastWasNumeric) words[words.length - 1] += `.${token}`
    else words.push(token)
    lastWasNumeric = numeric
  })
  return words
    .map((w) => (w === 'gpt' ? 'GPT' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ')
}

const MULTI_MODEL_TOOLTIP =
  'More than one model answered on this platform in this run (provider fallback).'
const LEGACY_MODEL_TOOLTIP =
  "Model not recorded for this run. Showing the platform's standard model at that time."

/** Short label and tooltip for the model(s) behind one platform of a run.
 *  row: that platform's ai_simulation_run_models row, or null (legacy run without one).
 *  Several recorded ids: labels joined with " / " in stored order, without duplicates;
 *  the tooltip lists the exact ids. No row at all: the platform's legacy standard model.
 *  A row without an id (the engine stores the platform slug) means no call on that
 *  platform answered: no label. */
export function describePlatformModel(row, platformId) {
  const recorded = getRecordedModelId(row)
  if (recorded) {
    const ids = recorded.split(',').map((s) => s.trim()).filter(Boolean)
    const labels = [...new Set(ids.map(formatModelLabel).filter(Boolean))]
    return {
      label: labels.join(' / '),
      title: ids.length > 1 ? `${ids.join(', ')}\n${MULTI_MODEL_TOOLTIP}` : ids[0],
    }
  }
  if (row != null) return null
  const legacyId = LEGACY_DEFAULT_MODEL_ID[platformId]
  return legacyId ? { label: formatModelLabel(legacyId), title: LEGACY_MODEL_TOOLTIP } : null
}
